import {
  Button,
  column,
  Css,
  GridColumn,
  GridDataRow,
  GridTable,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SelectField,
  simpleHeader,
  useModal,
} from "@homebound/beam";
import { useEffect, useRef, useState } from "react";
import { useAlertContext } from "src/components/AlertContext";
import { ShownApiError, useController } from "src/hooks/useController";
import {
  AmenityTrainingCoefficient,
  AmenityTrainingJob,
  UwConfigExport,
  UwConfigExportEndpoint,
  UwConfigImportEndpoint,
  UwConfigOptions,
  UwConfigRow,
  UwConfigStartTrainingEndpoint,
  UwConfigTrainingJobEndpoint,
  UwConfigTrainingJobsEndpoint,
} from "./UwConfigEndpoints";
import { UwConfigImportPreview } from "./UwConfigImportExport";
import { formatDate } from "./UwConfigRowModal";
import { ALL_PROPERTY_TYPES, UwConfigTypeDef } from "./uwConfigFields";

export const TRAINING_POLL_MS = 15_000;

/** The trained model that's a metro's adjustments for all property types (the others are property type specific) */
const ALL_PROPERTY_TYPES_MODEL = "single_family";

type AmenityTrainingModalProps = {
  typeDef: UwConfigTypeDef;
  options: UwConfigOptions;
  /** the current amenity adjustments, to compare the new coefficients to */
  rows: UwConfigRow[];
};

/** Train a metro's amenity models on the latest complete month, then review and apply the new coefficients */
export function AmenityTrainingModal({ typeDef, options, rows }: AmenityTrainingModalProps) {
  const { closeModal, openModal } = useModal();
  const { fetch } = useController();
  const { showError } = useAlertContext();
  const metros = options.trainable_metro ?? [];
  const [metro, setMetro] = useState<string | undefined>(metros[0]);
  const [job, setJob] = useState<AmenityTrainingJob | undefined>();
  const [busy, setBusy] = useState(false);
  // responses can arrive after another metro is picked; only the selected metro's jobs are shown (and applied)
  const selectedMetro = useRef(metro);
  selectedMetro.current = metro;

  // show the metro's latest training, e.g. one started earlier that's still running
  useEffect(() => {
    setJob(undefined);
    if (!metro) return;
    fetch(UwConfigTrainingJobsEndpoint, { metro })
      // unless training was started meanwhile
      .then(({ jobs }: { jobs: AmenityTrainingJob[] }) => showJob(metro, (current) => current ?? jobs[0]))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metro]);

  // poll while training
  useEffect(() => {
    if (job?.status !== "running") return;
    const timer = setInterval(() => {
      fetch(UwConfigTrainingJobEndpoint, { jobId: job.id })
        .then((latest: AmenityTrainingJob) =>
          // an earlier poll answering late can't undo a finished job (jobs never go back to running)
          showJob(latest.metro, (current) =>
            current?.id === latest.id && current.status === "running" ? latest : current,
          ),
        )
        .catch(() => {});
    }, TRAINING_POLL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, job?.status]);

  function showJob(
    forMetro: string,
    update: (current: AmenityTrainingJob | undefined) => AmenityTrainingJob | undefined,
  ) {
    if (forMetro !== selectedMetro.current) return;
    setJob((current) => {
      const next = update(current);
      return next && next.metro !== forMetro ? current : next;
    });
  }

  async function onTrain() {
    if (!metro) return;
    setBusy(true);
    try {
      const started: AmenityTrainingJob = await fetch(UwConfigStartTrainingEndpoint, { metro });
      showJob(metro, () => started);
    } catch (e) {
      showUnshownError(`Couldn't start training ${metro}`, e);
    } finally {
      setBusy(false);
    }
  }

  function showUnshownError(message: string, e: unknown) {
    // useController already showed the API's own errors, but not e.g. network failures
    if (!(e instanceof ShownApiError)) showError(`${message}: ${e instanceof Error ? e.message : String(e)}`);
  }

  async function onReview() {
    setBusy(true);
    try {
      const exported: UwConfigExport = await fetch(UwConfigExportEndpoint, { configType: typeDef.type });
      const document = withTrainedCoefficients(exported, job!);
      const preview = await fetch(UwConfigImportEndpoint, { configType: typeDef.type, document, dryRun: true });
      // the normal import preview, so applying them is logged, versioned and announced like any other change
      openModal({
        content: <UwConfigImportPreview typeDef={typeDef} document={document} preview={preview} />,
        size: "xl",
      });
    } catch (e) {
      showUnshownError("Couldn't preview the new coefficients", e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <ModalHeader>Train New Amenity Coefficients</ModalHeader>
      <ModalBody>
        <div css={Css.dg.gap2.$}>
          <div css={Css.sm.gray700.$}>
            Trains the metro's amenity models on its most recent complete month of sales. This takes a while; you can
            close this and come back. The new coefficients aren't used until you apply them.
          </div>
          <SelectField
            label="Metro"
            value={metro}
            onSelect={setMetro}
            options={metros.map((m) => ({ id: m, name: m }))}
            // a review in progress is for the shown metro's job
            disabled={busy}
          />
          {job && <TrainingStatus job={job} />}
          {job?.status === "succeeded" && job.result && <TrainingResults job={job} rows={rows} />}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" label="Close" onClick={closeModal} />
        <Button
          variant={job?.status === "succeeded" ? "secondary" : "primary"}
          label={job ? "Train Again" : "Train"}
          onClick={onTrain}
          disabled={!metro || busy || job?.status === "running"}
        />
        {job?.status === "succeeded" && <Button label="Review & Apply" onClick={onReview} disabled={busy} />}
      </ModalFooter>
    </>
  );
}

function TrainingStatus({ job }: { job: AmenityTrainingJob }) {
  const month = formatMonth(job.training_month);
  const who = `${job.requested_by ?? "someone"} on ${formatDate(job.created_at)}`;
  return (
    <div css={Css.sm.$} data-testid="trainingStatus">
      {job.status === "running" && `Training as of ${month}, started by ${who}…`}
      {job.status === "succeeded" && `Trained as of ${month}, started by ${who}.`}
      {job.status === "failed" && (
        <div css={Css.red600.$}>
          Training as of {month} (started by {who}) failed: {job.error}
        </div>
      )}
    </div>
  );
}

type ResultRow = { kind: "header" } | { kind: "data"; data: ResultData };
type ResultData = AmenityTrainingCoefficient & { propertyType: string; current?: number };

function TrainingResults({ job, rows }: { job: AmenityTrainingJob; rows: UwConfigRow[] }) {
  const data: ResultData[] = job.result!.models.flatMap((model) =>
    model.coefficients.map((c) => {
      const propertyType = model.property_type === ALL_PROPERTY_TYPES_MODEL ? null : model.property_type;
      const current = rows.find(
        (r) => r.adjustment_key === c.adjustment_key && r.metro === job.metro && r.property_type === propertyType,
      );
      return { ...c, propertyType: propertyType ?? ALL_PROPERTY_TYPES, current: current?.adjustment_value };
    }),
  );
  return (
    <GridTable
      columns={resultColumns}
      rows={
        [
          simpleHeader,
          ...data.map((d, i) => ({ kind: "data" as const, id: `${i}`, data: d })),
        ] as GridDataRow<ResultRow>[]
      }
      style={{ allWhite: true }}
    />
  );
}

const resultColumns: GridColumn<ResultRow>[] = [
  column<ResultRow>({ header: "Property Type", data: ({ propertyType }) => propertyType, w: 2 }),
  column<ResultRow>({ header: "Adjustment", data: ({ adjustment_key }) => adjustment_key, w: 2 }),
  column<ResultRow>({ header: "Current", data: ({ current }) => (current === undefined ? "—" : `${current}`), w: 1 }),
  column<ResultRow>({ header: "New", data: ({ value }) => `${value}`, w: 1 }),
  column<ResultRow>({ header: "95% CI", data: ({ ci_min, ci_max }) => `${ci_min} to ${ci_max}`, w: 2 }),
  column<ResultRow>({
    header: "Clip",
    data: ({ clip_min, clip_max }) => (clip_min === null && clip_max === null ? "—" : `${clip_min} to ${clip_max}`),
    w: 1,
  }),
];

/** The export with the trained coefficients (and their clip bounds) in place of the metro's current ones */
export function withTrainedCoefficients(exported: UwConfigExport, job: AmenityTrainingJob): UwConfigExport {
  const config = JSON.parse(JSON.stringify(exported.config));
  const source = `trained as of ${job.training_month} (uw config training ${job.id})`;
  for (const model of job.result!.models) {
    for (const c of model.coefficients) {
      const byMetro = (config[c.adjustment_key] ??= {});
      const metroEntry = (byMetro[job.metro] ??= {});
      const target =
        model.property_type === ALL_PROPERTY_TYPES_MODEL
          ? metroEntry
          : ((metroEntry.property_types ??= {})[model.property_type] ??= {});
      Object.assign(target, {
        adjustment_type: "percentage",
        adjustment_value: c.value,
        clip_min: c.clip_min,
        clip_max: c.clip_max,
        source,
      });
    }
  }
  return { ...exported, config };
}

function formatMonth(isoDate: string): string {
  const [year, month] = isoDate.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" });
}
